import { motion } from "framer-motion";
import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { IoCloseOutline } from "react-icons/io5";
import { HiChevronLeft } from "react-icons/hi";
import { Button, Dropdown, Input } from "semantic-ui-react";

import { animatePage } from "../../animateConstant";
import InputComponents from "../../components/InputComponents/InputComponents";
import { CURRENCY_SYMBOL, DEPOSIT_AMOUNT } from "../../constant";
import InputCustom from "../../components/InputComponents/InputCustom";
import CloseComponent from "../../components/CloseComponent/CloseComponent";
import GetParams from "../../utils/getParams";
import ErrorHint from "../../components/Hint/ErrorHint";
import { loading, loadingDestroy, notice } from "../../redux/action/action";
import { storeDepositLink } from "../../redux/action/cashflow/cashflowAction";
import CloseDetailPage from "../../components/CloseComponent/CloseDetailPage";
import LazyImage from "../../components/Loading/lazyImage";
import { depositPayment, depositVipPayment } from "../../api/postApi";
import NormalBankPayment from "./components/normalBankPayment";
import VipBankPayment from "./components/vipBankPayment";
import { formatNumber } from "../../utils/formatNumber";
import CryptoPayment from "./components/cryptoPayment";

const DepositSteoTwo = ({
  chooseCurrency,
  setDepositStep,
  depositStep,
  paymentData,
}) => {
  const { t } = useTranslation();
  const i18n = (key) => t(`deposit.${key}`);

  const [data, setData] = useState({});
  const [dataFocus, setDataFocus] = useState({});
  const [bankPaymentSelected, setBankPaymentSelected] = useState({}); //銀行的渠道

  const navigate = useNavigate();
  const location = useLocation();

  const dispatch = useDispatch();

  const method = GetParams("method");
  const min = Number(GetParams("min"));
  const max = Number(GetParams("max"));
  const isCrypto = GetParams("isCrypto") == 1;

  const isVip = useMemo(() => {
    if (isCrypto) {
      return false;
    }
    return paymentData?.bank?.isVIP === 1;
  }, [paymentData]);

  const morePaymentList = useMemo(() => {
    return paymentData?.[isCrypto ? "crypto" : method]?.list;
  }, [method]); //更多提款方式

  const paymentList = useMemo(() => {
    return Object.keys(morePaymentList || {});
  }, [morePaymentList]);

  const amountCondition = useMemo(() => {
    const amounts = Number(data.amounts);
    if (amounts) {
      if (amounts < min || amounts > max) {
        return true;
      } else {
        return false;
      }
    }
  }, [data.amounts]);

  const othersCondition = useMemo(() => {
    if (isVip) {
      if (!bankPaymentSelected.bank_name) {
        return true;
      } else {
        return false;
      }
    } else {
      const pay_way = bankPaymentSelected?.bankcode;
      const channel_code = bankPaymentSelected?.code;

      if (!pay_way || !channel_code) {
        return true;
      } else {
        return false;
      }
    }
  }, [bankPaymentSelected]);

  const handleSubmit = () => {
    dispatch(loading());

    if (isVip) {
      depositVipPayment({
        amount: data.amounts,
        bank_uid: bankPaymentSelected?.uid,
        bank_number_last5: bankPaymentSelected.bank_num.slice(-5),
      })
        .then((data) => {
          dispatch(notice("Success", "success"));
          navigate("deposit-success");
        })
        .catch((err) => {})
        .finally(() => {
          dispatch(loadingDestroy());
        });
    } else {
      depositPayment({
        amount: data.amounts,
        pay_type: method,
        pay_way: bankPaymentSelected?.bankcode,
        channel_code: bankPaymentSelected?.code,
      })
        .then((data) => {
          dispatch(storeDepositLink(data.url));
          navigate("depositfund" + location.search);
        })
        .catch((err) => {
          dispatch(notice(err.message, "error"));
        })
        .finally(() => {
          dispatch(loadingDestroy());
        });
    }
  };

  const handleChange = (e) => {
    localStorage.setItem("amounts", e.target.value);
    setData({ ...data, [e.target.name]: e.target.value });
  };
  const handleFocus = (e) => {
    setDataFocus({ [e.target.name]: true });
  };
  return (
    <motion.section
      className="h-auto flex flex-col justify-between"
      initial={depositStep === 1 ? "hiddenRight" : "hiddenLeft"}
      animate="visible"
      exit={depositStep === 1 ? "hiddenRight" : "hiddenLeft"}
      variants={animatePage}
    >
      <section>
        <CloseDetailPage
          hasPrev={
            <HiChevronLeft
              className="cursor-pointer"
              onClick={() => setDepositStep(1)}
            />
          }
          title={
            <span className="flex items-center justify-center">
              {method?.toUpperCase()}
              {isVip && (
                <span className="ml-[5px] bg-gold-text text-xs rounded-full px-[7px] py-[1px]">
                  VIP
                </span>
              )}
            </span>
          }
        />

        <div className="flex items-center justify-center mb-[30px]">
          <LazyImage
            src={`https://gamepics.91url.cc/bankIcon/${method?.toLowerCase()}.png?123123`}
            className="!w-[50px] !h-[50px] object-cover"
            loadingClassName=""
          />
        </div>

        <section>
          <p className="text-center text-transaction-text dark:text-base-light-white my-[20px]">
            {i18n("enterdetail")}
            {chooseCurrency}.
          </p>

          <p className="text-xl font-semibold text-transaction-text dark:text-base-light-white">
            {i18n("deposit")} {chooseCurrency}
          </p>
          <section className="flex flex-col gap-[20px]">
            {!isCrypto && (
              <section className="grid items-center grid-cols-3 gap-[8px] ">
                {DEPOSIT_AMOUNT.map((item, index) => {
                  return (
                    <Button
                      key={index}
                      className="!bg-main-color  !text-light-white text !hover:text-transparent !px-[3px]"
                      content={`${formatNumber(item.amount, {
                        point: false,
                      })}`}
                      onClick={() => {
                        localStorage.setItem("amounts", item.amount);
                        setData({ ...data, amounts: item.amount });
                      }}
                    />
                  );
                })}
              </section>
            )}

            {isCrypto ? (
              <CryptoPayment paymentsDetail={morePaymentList?.[method]} />
            ) : isVip ? (
              <VipBankPayment
                morePaymentList={morePaymentList}
                bankPaymentSelected={bankPaymentSelected}
                setBankPaymentSelected={setBankPaymentSelected}
              />
            ) : (
              morePaymentList && (
                <NormalBankPayment
                  paymentList={paymentList}
                  morePaymentList={morePaymentList}
                  setBankPaymentSelected={setBankPaymentSelected}
                />
              )
            )}
            {!isCrypto && (
              <section>
                <span className="text-transaction-text dark:text-base-light-white">
                  {i18n("amounts")}
                </span>
                <InputCustom
                  placeholder={`${i18n("Enter your")}  ${chooseCurrency} ${i18n(
                    "amount"
                  )}`}
                  type="number"
                  name="amounts"
                  setDataProps={setData}
                  dataProps={data}
                  onChange={(e) => handleChange(e)}
                  onFocus={(e) => handleFocus(e)}
                  focusProps={dataFocus}
                />
              </section>
            )}
          </section>
          <section className="mb-[10px]"></section>
        </section>
      </section>
      <section>
        {amountCondition && (
          <ErrorHint>
            The withdrawal amount should fall within the range of{" "}
            {CURRENCY_SYMBOL}
            {min} to {CURRENCY_SYMBOL}
            {max}.
          </ErrorHint>
        )}
        {data.amounts && (
          <p className="text-transaction-text dark:text-base-light-white">
            {chooseCurrency}{" "}
            <span className="text-main-color font-bold">
              {formatNumber(data.amounts, { point: false })}
            </span>{" "}
            In your personal account
          </p>
        )}
        {!isCrypto && (
          <Button
            onClick={handleSubmit}
            disabled={amountCondition || othersCondition}
            className="!bg-main-color !text-white w-full"
            content={i18n("deposit")}
            size="big"
          />
        )}
      </section>
    </motion.section>
  );
};

export default DepositSteoTwo;
